/** WebSocket action constants (WS_ACTIONS). DOM_EVENTS lives in websocket-actions-dom.ts (browser CustomEvent names --
 * a different vocabulary) and the wishlist-domain subset in websocket-actions-wishlist.ts; `WS_ACTIONS` spreads the
 * wishlist subset back in so protocol consumers see a single flat object.
 *
 * A NEW MEMBER OWES BOTH ENDS -- a producer and a reader. The auction transition the property-detail surface observes
 * is `auctionStateUpdated` (emitted by `broadcastAuctionStateChange` from the mark-won / mark-lost / cancel flows), and
 * its detail facts arrive on the six `*_UPDATED` fact actions, so there is no separate details-updated, won or lost
 * action; there is no activity updated/deleted broadcast (no client handles one), and no wishlist request/reply pair
 * (no client handles its reply).
 *
 * NOT EVERY MEMBER IS TWO-ENDED, and the distinction is deliberate rather than an oversight. A CENSUS OF THE
 * REMAINING SURFACE FOUND NO IN-REPO SENDER for six INBOUND actions -- `getAlarms`, `setAlarms`,
 * `makeHighestBidPropertyDetails` (PropertyDO) and `auctionFilter`, `searchAuctions`, `unsubscribeProperties` (UserDO)
 * -- and no in-repo READER for the `alarms` reply. These are NOT cut, on two grounds. An inbound action is driven by a
 * CLIENT, and `docs/architecture/system-overview.mmd` declares external Web/Mobile/API and WebSocket clients, so
 * in-repo silence cannot establish that nothing sends one. And `alarms` is the reply arm of the retained `getAlarms`:
 * deleting a reply while keeping the request it answers would break a live pair, not remove dead weight. Cutting any of
 * the seven is an operator decision that needs evidence from outside this repo.
 */

import { WS_WISHLIST_ACTIONS } from '@federa/const/websocket-actions-wishlist';

export const WS_ACTIONS = {
	DATA_RESPONSE: 'dataResponse',
	OPERATION_SUCCESS: 'operationSuccess',
	/** Wire MESSAGE-TYPE vocabulary; byte-coincides with WS_STATE.ERROR (const/websocket-connection-states.ts), a connection-STATE enum member -- different axes, never fold. */
	ERROR: 'error',
	READY: 'ready',
	KEEP_ALIVE: 'keepAlive',
	PING: 'ping',
	PONG: 'pong',
	ALIVE_RECEIVED: 'aliveReceived',
	AUTHENTICATE: 'authenticate',
	AUTHENTICATION_SUCCESS: 'authenticationSuccess',
	AUCTION_STARTED: 'auctionStarted',
	AUCTION_FINISHED: 'auctionFinished',
	AUCTION_EXTENDED: 'auctionExtended',
	AUCTION_STATE_UPDATED: 'auctionStateUpdated',
	CURRENT_BALANCE_UPDATED: 'Current_BalanceUpdated',
	NEW_BID: 'newBid',
	HIGHEST_BID: 'highestBid',
	HIGHEST_BID_UPDATED: 'highestBidUpdated',
	BIDS_UPDATED: 'bidsUpdated',
	BUY_SIDE_OFFER_CREATED: 'buySideOfferCreated',
	SUBSCRIBE: 'subscribe',
	SEARCH: 'search',
	CLOSE_CHANNEL: 'closeChannel',
	AUCTION_FILTER: 'auctionFilter',
	PROPERTY_FILTER: 'propertyFilter',
	RESET_ALL_PROPERTIES: 'resetAllProperties',
	SEARCH_AUCTIONS: 'searchAuctions',
	SUBSCRIBE_PROPERTIES: 'subscribeProperties',
	UNSUBSCRIBE_PROPERTIES: 'unsubscribeProperties',
	DATA_OPERATION: 'dataOperation',
	GET_DATA: 'getData',
	UPDATE_AUCTION_STATE: 'updateAuctionState',
	MAKE_HIGHEST_BID_PROPERTY_DETAILS: 'makeHighestBidPropertyDetails',
	GET_ALARMS: 'getAlarms',
	SET_ALARMS: 'setAlarms',
	EXTEND_AUCTION: 'extendAuction',
	...WS_WISHLIST_ACTIONS,
	ALARMS: 'alarms',
	GET_PROPERTY_FACTS: 'getPropertyFacts',
	PROPERTY_FACTS: 'propertyFacts',
	STRUCTURE_UPDATED: 'structureUpdated',
	LAND_UPDATED: 'landUpdated',
	UTILITIES_UPDATED: 'utilitiesUpdated',
	TAXES_UPDATED: 'taxesUpdated',
	COMMUNITY_UPDATED: 'communityUpdated',
	TAX_HISTORY_UPDATED: 'taxHistoryUpdated',
	LEDGER_UPDATED: 'ledgerUpdated',
} as const;
